
    import Vue from "vue";
    import Component from "vue-class-component";
    import {ApprovalStatus, factory, ResultError, SeafarerActions, SeafarerBasicInfoModel, SeafarersClient} from "@/api-client";
    import {NavigationGuardNext, RawLocation} from "vue-router/types/router";
    import {Prop} from "vue-property-decorator";
    import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";

    class NavTab{
        constructor(public readonly name: string, public readonly location: RawLocation) {
        }
    }

    interface Props {
        seafarerActions: SeafarerActions;
        seafarerId: string;
        preLoadedBasicInfo?: SeafarerBasicInfoModel;
    }

    @Component({
      components: {
        ButtonWithConfirm
      },
        async beforeRouteEnter(to, from, next: NavigationGuardNext<PersonProfile>) {
          const props = to.meta!.props as Props;

          try {
              props.seafarerId = to.params.seafarerId;
              props.seafarerActions = await new SeafarersClient().availableActions(to.params.seafarerId);
              if(props.seafarerActions.canViewBasicInfo){
                  props.preLoadedBasicInfo = await new SeafarersClient().basicInfo(to.params.seafarerId);
              }
              next();
          } catch (e) {
              next(e);
          }
        }
    })
    export default class PersonProfile extends Vue implements Props {
        @Prop({required: true})
        seafarerActions!: SeafarerActions;
        @Prop({required: true})
        seafarerId!: string;
        @Prop()
        preLoadedBasicInfo!: SeafarerBasicInfoModel;

        basicInfo: SeafarerBasicInfoModel = this.preLoadedBasicInfo;
        errors: ResultError = factory.create(ResultError);
        canActivate = this.seafarerActions.canActivateSeafarer;
        canDeactivate = this.seafarerActions.canDeactivateSeafarer;

        approvalStatuses = ApprovalStatus;

        async refreshBasicInfo(){
            this.errors = factory.create(ResultError);
            if(this.seafarerActions.canViewBasicInfo) {
                this.basicInfo = await new SeafarersClient().basicInfo(this.seafarerId);
            }

            const refreshedActions = await new SeafarersClient().availableActions(this.seafarerId);
            this.canActivate = refreshedActions.canActivateSeafarer;
            this.canDeactivate = refreshedActions.canDeactivateSeafarer;
        }

        async deactivate() {
          await new SeafarersClient().deactivate(this.seafarerId).then(
              async () => await this.refreshBasicInfo(),
              error => this.setValidationErrors(error, null, "errors"));
        }

        async activate() {
          await new SeafarersClient().activate(this.seafarerId).then(
              async () => await this.refreshBasicInfo(),
              error => this.setValidationErrors(error, null, "errors"));
        }

        created(){
            this.$watch(() => this.$route.name, () => {
                if(this.$route.name === 'person-profile') {
                    this.$router.replace(this.getNavigationTabs[0].location);
                }
            }, {immediate: true})
        }

        get getNavigationTabs() {
            const seafarerId = this.$route.params.seafarerId;

            const tabs:NavTab[] = [];

            if(this.seafarerActions.canViewPersonalInformation){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_PersonalInfo, { name: 'personal-info', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewQualifications){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_Qualifications, { name: 'seafarer-qualifications', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewMedicalCertificates){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_MedicalCertificates, { name: 'medical-certificates', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewPractices){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_Practices, { name: 'seafarer-practices', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewCertificates){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_Certificates, { name: 'certificates', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewEndorsements){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_Endorsements, { name: 'endorsements', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewAllDocuments){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_AllDocuments, { name: 'all-documents', params: {seafarerId} }))
            }
            if(this.seafarerActions.canViewSharedResources){
                tabs.push(new NavTab(this.$ls.PersonProfile_Profile_Nav_SharedResources, { name: 'seafarer-shared-resources', params: {seafarerId} }))
            }

            return tabs;
        }
    }
